import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

/**
 * Owns announcement timers. Consumers decide which transitions should be announced.
 */
export function useStatusAnnouncement(): {
	statusText: ReactNode;
	announce: ({
		text,
		delay,
		clearAfter,
	}: {
		text: ReactNode;
		delay?: number;
		clearAfter?: number;
	}) => void;
	cancelPendingAnnouncement: () => boolean;
	resetStatus: () => void;
} {
	const [statusText, setStatusText] = useState<ReactNode>('');
	const announceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
	const clearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const cancelPendingAnnouncement = useCallback((): boolean => {
		if (announceTimeout.current === null) {
			return false;
		}
		clearTimeout(announceTimeout.current);
		announceTimeout.current = null;
		return true;
	}, []);

	const cancelClear = useCallback(() => {
		if (clearTimeoutRef.current !== null) {
			clearTimeout(clearTimeoutRef.current);
			clearTimeoutRef.current = null;
		}
	}, []);

	const resetStatus = useCallback((): void => {
		cancelPendingAnnouncement();
		cancelClear();
		setStatusText('');
	}, [cancelPendingAnnouncement, cancelClear]);

	const announce = useCallback(
		({
			text,
			delay = 0,
			clearAfter,
		}: {
			text: ReactNode;
			delay?: number;
			clearAfter?: number;
		}): void => {
			cancelPendingAnnouncement();
			cancelClear();
			const publish = () => {
				announceTimeout.current = null;
				setStatusText(text);
				if (clearAfter !== undefined) {
					clearTimeoutRef.current = setTimeout(() => {
						setStatusText('');
						clearTimeoutRef.current = null;
					}, clearAfter);
				}
			};
			if (delay === 0) {
				publish();
			} else {
				announceTimeout.current = setTimeout(publish, delay);
			}
		},
		[cancelPendingAnnouncement, cancelClear],
	);

	useEffect(
		() => () => {
			cancelPendingAnnouncement();
			cancelClear();
		},
		[cancelPendingAnnouncement, cancelClear],
	);

	return { statusText, announce, cancelPendingAnnouncement, resetStatus };
}
